import { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  registerUser,
  registerGoogleUser,
  initializeUserAccount,
  getUserProfile,
  getUserPlan,
  syncVerifiedUserProfile,
  signOutUser as signOutUserApi,
  signInUser as signInUserApi,
  deleteCurrentUserAccount,
} from "../../api/users.api";

const USER_STORAGE_KEY = "postly_user";

export const UserContext = createContext({
  user: null,
  currentUser: null,
  setUser: () => null,
  setCurrentUser: () => null,
  userPlan: null,
  loading: false,
  isAuthenticated: false,
  loginUser: () => null,
  resetUserState: () => null,
  hydrateUserProfile: async () => null,
  signUpUser: async () => null,
  googleSignUp: async () => null,
  googleSignIn: async () => null,
  logoutUser: async () => null,
  removeCurrentUserAccount: async () => null,
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentUser = user;
  const isAuthenticated = !!user;

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem(USER_STORAGE_KEY);

      if (!storedUser) return;

      const parsedUser = JSON.parse(storedUser);

      if (parsedUser?.uid) {
        setUser(parsedUser);
        setUserPlan(parsedUser?.plan || null);
      }
    } catch (error) {
      console.error("Failed to rehydrate user from localStorage:", error);
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const refreshStoredUser = async () => {
      try {
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);
        if (!storedUser) return;

        const parsedUser = JSON.parse(storedUser);
        if (!parsedUser?.uid) return;

        await hydrateUserProfile(parsedUser.uid);
      } catch (error) {
        console.error(
          "Failed to refresh stored user profile:",
          error?.response?.data || error.message
        );
      }
    };

    refreshStoredUser();
  }, []);

  const persistUserToStorage = (userData) => {
    if (!userData) return;
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
  };

  const removeUserFromStorage = () => {
    localStorage.removeItem(USER_STORAGE_KEY);
  };

  const setCurrentUser = (userData) => {
    setUser(userData);
    setUserPlan(userData?.plan || null);
    persistUserToStorage(userData);
  };

  const loginUser = (userData) => {
    setUser(userData);
    setUserPlan(userData?.plan || null);
    persistUserToStorage(userData);
  };

  const resetUserState = () => {
    setUser(null);
    setUserPlan(null);
    removeUserFromStorage();
  };

  const hydrateUserProfile = async (uid) => {
    if (!uid) {
      throw new Error("uid is required to hydrate user profile.");
    }

    const profile = await getUserProfile(uid);
    const planData = await getUserPlan(uid).catch(() => null);

    const hydratedUser = profile || null;
    const hydratedPlan = planData?.plan || profile?.plan || null;

    setUser(hydratedUser);
    setUserPlan(hydratedPlan);

    if (hydratedUser) {
      persistUserToStorage({
        ...hydratedUser,
        plan: hydratedPlan,
      });
    }

    return hydratedUser;
  };

  const signUpUser = async ({ email, password, plan = "freemium" }) => {
    try {
      setLoading(true);

      const registerResponse = await registerUser({ email, password });

      if (!registerResponse?.success || !registerResponse?.user) {
        throw new Error(registerResponse?.error || "Unable to register user.");
      }

      const { uid, email: registeredEmail } = registerResponse.user;

      await initializeUserAccount({
        uid,
        email: registeredEmail,
        plan,
      });

      await syncVerifiedUserProfile(registeredEmail);

      return {
        success: true,
        uid,
        email: registeredEmail,
      };
    } catch (error) {
      console.error(
        "signUpUser error:",
        error?.response?.data || error.message
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInUser = async ({ email, password }) => {
    try {
      setLoading(true);

      const response = await signInUserApi({ email, password });
      const signedInUser = response?.user;

      if (!signedInUser) {
        throw new Error("No user returned from sign in.");
      }

      loginUser(signedInUser);

      return response;
    } catch (error) {
      console.error(
        "signInUser context error:",
        error?.response?.data || error.message
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const completeGoogleAuth = async ({ credential, mode }) => {
    try {
      setLoading(true);

      const googleResponse = await registerGoogleUser({
        credential,
        mode,
      });

      if (!googleResponse?.success || !googleResponse?.user) {
        throw new Error(
          googleResponse?.error || "Unable to authenticate with Google."
        );
      }

      const { uid, email } = googleResponse.user;

      const profile = await hydrateUserProfile(uid);
      const finalUser = profile || googleResponse.user;

      loginUser(finalUser);

      return {
        success: true,
        user: profile || googleResponse.user,
      };
    } catch (error) {
      console.error(
        "signInUser context error:",
        error?.response?.data || error.message
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const googleSignUp = async (credential) => {
    return completeGoogleAuth({
      credential,
      mode: "signup",
    });
  };

  const googleSignIn = async (credential) => {
    return completeGoogleAuth({
      credential,
      mode: "signin",
    });
  };

  const logoutUser = async () => {
    try {
      if (!user?.uid) {
        console.warn("No user found to sign out.");
        return;
      }

      const response = await signOutUserApi({ uid: user.uid });
      // console.log("Signed out user:", response.user);

      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }
    } catch (error) {
      console.error("Sign out error:", error?.response?.data || error.message);
      throw error;
    } finally {
      resetUserState();
    }
  };

  const removeCurrentUserAccount = async (uid) => {
    try {
      setLoading(true);

      if (!uid) {
        throw new Error("uid is required to delete user account.");
      }

      const response = await deleteCurrentUserAccount(uid);
      resetUserState();
      return response;
    } catch (error) {
      console.error(
        "DELETE USER ERROR:",
        error?.response?.data || error.message
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({
      user,
      currentUser,
      setUser,
      setCurrentUser,
      userPlan,
      setUserPlan,
      loading,
      isAuthenticated,
      loginUser,
      signInUser,
      resetUserState,
      hydrateUserProfile,
      signUpUser,
      googleSignUp,
      googleSignIn,
      logoutUser,
      removeCurrentUserAccount,
    }),
    [user, currentUser, userPlan, loading, isAuthenticated]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
